import {Component, Output, EventEmitter, ViewChild, ChangeDetectorRef} from "@angular/core";
import {GlossaryService} from "../services/glossary/glossary.service";
import {IGlossaryComponent} from "../services/glossary/iglossary.component";
import {EventBus} from "../event/EventBus";
import {ActivityEvent, ACTIVITY_STATUS, ActivityEventData, ActivityModalEvent} from "../event/ActivityEvent";
import {ClientConfiguration} from "../services/configuration/client.config";
import {StudentConfiguration} from "../services/assignment/student.configuration";
import {Scilearn} from "../constants";
import {SessionService} from "../services/configuration/session.service";
import {ContentCursor} from "../services/content/content.cursor";
import {BugsnagLoggingService} from "../services/logging/bugsnag-logging.service";
import {ComponentBase} from "../base/ComponentBase";
import {TimerStrategy} from "../services/configuration/timer.config";
import {RaVersion} from "../services/configuration/ra.version";
import {AudioQueueService} from "../services/audio-playback/audioqueue.service";
import {AudioQueueItem, TranslateAudio} from "../services/audio-playback/audioqueue.item";
import {AudioQueueEvent, InterruptAudio} from "../event/AudioQueueEvent";
import {filter} from "rxjs/operators";

@Component({
    selector: "glossary",
    templateUrl: "./glossary.component.html",
    styleUrls: ["./glossary.component.scss"]
})

export class GlossaryComponent extends ComponentBase implements IGlossaryComponent {

    private continueReading: boolean = false;
    private glossaryStart: Date;
    private glossaryEnd: Date;
    private videoTimestamp: number;
    private videoObject: any;
    private videoElement: HTMLElement;
    private videoEndedEvent: EventListenerOrEventListenerObject;
    private videoCanPlayEvent: EventListenerOrEventListenerObject;
    public glossary: any;
    public spanishEnabled: boolean = false;
    public hideCloseButton: boolean = false;
    public showGlossaryVideo: boolean = false;
    public highlight = {
        word: false,
        definition: false,
        context: false,
        imagery: false,
        didYouKnow: false,
        spanish: false
    };
    public tab = {
        glossary: true,
        imagery: false,
        context: false,
        didYouKnow: false
    };
    public spanish: any;
    public automatic: boolean = false;

    @Output() public closeGlossaryEvent = new EventEmitter();

    constructor(private eventBus: EventBus,
                private glossaryService: GlossaryService,
                private config: ClientConfiguration,
                private studentConfig:StudentConfiguration,
                private audioQueueService: AudioQueueService,
                private session: SessionService,
                private contentCursor: ContentCursor,
                private log:BugsnagLoggingService,
                private changeDetectorRef: ChangeDetectorRef) {
        super("GlossaryComponent");
        this.subscribeToEventBus();
    }

    private resetHighlight(): void {
        this.highlight = {
            word: false,
            definition: false,
            imagery: false,
            context: false,
            didYouKnow: false,
            spanish: false
        };
    }

    private resetTab(): void {
        this.tab = {
            glossary: true,
            imagery: false,
            context: false,
            didYouKnow: false
        };
    }

    private resetGlossaryState(): void {
        this.resetHighlight();
        this.resetTab();
        this.spanishEnabled = false;
        this.glossaryService.setGlossaryOpened(true);
    }

    private subscribeToEventBus(): void {
        let sub = this.eventBus.pipe(filter((e: ActivityEvent) => e instanceof ActivityEvent))
            .subscribe((event: ActivityEvent) => {
                if (event.data.type === "glossary") {
                    if (event.status === ACTIVITY_STATUS.activity_end) {
                        this.closeGlossary();
                    } else if (event.data.word && (event.status === ACTIVITY_STATUS.activity_start)) {
                        this.audioQueueService.reset().subscribe(() => {
                            this.init(event);
                            if (!event.data.visualOnly) {
                                this.readText();
                            }
                            this.trackOutsideDidYouKnowClick();
                            if (this.contentCursor.selectedWord) {
                                this.contentCursor.selectGlossaryPhrase(event.data.word);
                            }
                        });
                    }
                }
            });
        this.subscriptions.push(sub);

        // subscribing to modal-open events so if we get a popup for a timeout, exit-request, etc, video
        // should stop talking
        let modalOpenEvent = this.eventBus.pipe(filter(event => event instanceof ActivityModalEvent))
            .subscribe((event:ActivityModalEvent) => {
            if (event.status === ACTIVITY_STATUS.modal_open && this.glossaryService.isGlossaryOpened()) {
                console.log("Resetting the video process with a modal open event.");
                this.resetVideoProcess();
            }
        });

        this.subscriptions.push(modalOpenEvent);
    }

    private init(event: ActivityEvent): void {
        this.resetVideoProcess();
        this.glossaryStart = new Date();
        this.automatic = event.data.automatic;
        this.resetGlossaryState();
        this.videoTimestamp = Date.now();
        this.glossary = this.glossaryService.getEntry(event.data.word.glossary);
        this.spanish =
            this.glossaryService.getTranslation("es", event.data.word.translations,
                                                this.glossary.id);
        this.hideCloseButton = event.data.hideCloseButton;
    }

    private trackOutsideDidYouKnowClick(): void {
        document.addEventListener("click", (event: any) => {
            let tooltipClicked = event.target.className.match("did-you-know-tooltip");
            let tooltipBtnClicked = event.target.className.match("did-you-know-btn");

            if (!tooltipClicked && !tooltipBtnClicked && this.tab.didYouKnow) {
                this.nextAudioActions("didYouKnow");
            }
        });
    }

    private resetAudio(): void {
        this.resetHighlight();
        this.resetTab();
        this.audioQueueService.hardReset();
    }

    private initiateUploadUserModel(): void {
        let glossaryDuration = this.glossaryEnd.getTime() - this.glossaryStart.getTime();
        this.session.sendBookSessionData({
            "durationSessionList": [{
                "activityType": Scilearn.__GLOSSARY__,
                "duration": { "durationTime": glossaryDuration }
            }]
        }).subscribe(() => {
            this.eventBus.next(new ActivityEvent(ACTIVITY_STATUS.one_time_activity, new ActivityEventData({timing: TimerStrategy.glossaryEndComplete})));
        });
    }

    private nextAudioActions(type: string): void {
        if (!this.glossaryService.isGlossaryOpened()) {
            return;
        }

        this.eventBus.next(
            new ActivityEvent(ACTIVITY_STATUS.activity_start, new ActivityEventData({ type: "glossary", audio: type }))
        );

        if (this.audioQueueService.isPlaying()) {
            this.resetAudio();
        }

        this.continueNextAction(type);
        this.changeDetectorRef.detectChanges();
    }

    private continueNextAction(type: string): void {
        if (this.continueReading) {
            if (type === "text") {
                this.readDefinition();
                this.highlight.word = false;
            } else if (type === "definition") {
                this.tab.glossary = false;
                this.tab.imagery = true;
                this.tab.context = false;
                this.playVideo();
                this.highlight.definition = false;
            } else if (type === "imagery") {
                this.tab.didYouKnow = false;
                this.tab.imagery = false;
                this.tab.context = true;
                this.readContext();
                this.highlight.imagery = false;
            } else if (type === "context") {
                this.tab.didYouKnow = true;
                this.tab.imagery = false;
                this.tab.context = false;
                this.readDidYouKnow(false);
                this.highlight.context = false;
            } else if (type === "didYouKnow") {
                this.continueReading = false;
                this.tab.glossary = true;
                this.tab.imagery = false;
                this.tab.didYouKnow = false;
                this.stopReading();
            }
        }
    }

    private setupVideoProcess(): void {
        let id = "glossyvideo";
        if (window.innerWidth < 960) {
            id = "horizontalglossyvideo";
            this.videoObject = this.horizontalglossyvideo.nativeElement;
        } else {
            this.videoObject = this.glossyvideo.nativeElement;
        }
        this.videoObject.load();
        this.videoElement = document.getElementById(id);
        this.showGlossaryVideo = true;

        this.videoCanPlayEvent = (): void => {
            this.audioQueueService.reset().subscribe(() => {
                this.log.log("Playing video url = " + this.videoObject.currentSrc);
                this.videoObject.play();
                // @ts-ignore
              this.videoElement.removeEventListener("canplay", this.videoCanPlayEvent, true);
            });
        };

        this.videoElement.addEventListener("canplay", this.videoCanPlayEvent, true);

        this.videoEndedEvent = (): void => {
            this.videoObject.pause();
            this.nextAudioActions("imagery");
        };

        this.videoElement.addEventListener("ended", this.videoEndedEvent, true);
    }

    private resetVideoProcess(): void {
        if (this.videoObject) {
            this.videoObject.pause();
            this.showGlossaryVideo = false;
            this.videoElement.removeEventListener("canplay", this.videoCanPlayEvent, true);
            this.videoElement.removeEventListener("ended", this.videoEndedEvent, true);
        }
    }

    public getVideoStyle(): {"opacity": string} {
        let visibility = this.showGlossaryVideo ? "1" : "0";
        return {"opacity": visibility};
    }

    public readText(): void {
        let audio = this.glossaryService.getTextAudio(this.glossary);
        let url = this.config.audioUrl + audio;

        this.audioQueueService.reset().subscribe(() => {
            this.resetVideoProcess();
            this.resetHighlight();
            this.resetTab();
            this.eventBus.startActivityEvent(new ActivityEventData({timing: TimerStrategy.glossaryAudio}));
            this.continueReading = true;
            this.highlight.word = true;

            this.eventBus.next(new AudioQueueEvent([
                new AudioQueueItem(url, TranslateAudio.NO, () => this.nextAudioActions("text"))
            ], InterruptAudio.NO));
        });
    }

    public stopReading(): void {
        this.continueReading = false;
        this.log.log("sending activity end");
        this.eventBus.endActivityEvent(new ActivityEventData({timing: TimerStrategy.glossaryAudio}));
    }

    public readDefinition(): void {
        if (this.glossary.definition && this.glossary.definition.audio) {
            let url = this.config.audioUrl + this.glossary.definition.audio;
            this.highlight.definition = true;
            this.eventBus.next(
                new AudioQueueEvent([
                    new AudioQueueItem(url, TranslateAudio.NO, () => this.nextAudioActions("definition"))
                ], InterruptAudio.YES));
        } else {
            this.nextAudioActions("definition");
        }
    }

    @ViewChild("glossyvideo") glossyvideo: any;
    @ViewChild("horizontalglossyvideo") horizontalglossyvideo: any;
    public playVideo(): void {
        if (this.glossary.context?.media?.search(/\.mov$/i) > 0 ) {
            this.highlight.imagery = true;
            this.setupVideoProcess();
        } else {
            this.nextAudioActions("imagery");
        }
    }

    public readContext(): void {
        if (this.glossary.context && this.glossary.context.audio) {
            let url = this.config.audioUrl + this.glossary.context.audio;
            this.highlight.context = true;
            this.eventBus.next(
                new AudioQueueEvent([
                    new AudioQueueItem(url, TranslateAudio.NO, () => this.nextAudioActions("context"))
                ], InterruptAudio.YES));
        } else {
            this.nextAudioActions("context");
        }
    }

    public readDidYouKnow(keepAlive: boolean): void {
        if (this.glossary.fact && this.glossary.fact.audio) {
            let url = this.config.audioUrl + this.glossary.fact.audio;
            this.resetAudio();
            this.highlight.didYouKnow = true;
            this.tab.didYouKnow = true;
            this.eventBus.startActivityEvent(new ActivityEventData({timing: TimerStrategy.glossaryFact}));

            this.eventBus.next(
                new AudioQueueEvent([
                    new AudioQueueItem(url, TranslateAudio.NO, () => {
                        if (this.automatic) {
                            this.nextAudioActions("didYouKnow");
                            this.eventBus.endActivityEvent(new ActivityEventData(
                                {timing: TimerStrategy.glossaryAudio}));
                        } else if (!keepAlive) {
                            this.nextAudioActions("didYouKnow");
                        } else {
                            this.highlight.didYouKnow = false;
                        }
                        this.eventBus.endActivityEvent(new ActivityEventData({timing: TimerStrategy.glossaryFact}));
                        this.tab.didYouKnow = false;
                    })
                ], InterruptAudio.YES));
        } else {
            this.nextAudioActions("didYouKnow");
        }
    }

    public emitCloseGlossaryEvent(): void {
        this.closeGlossaryEvent.next({word: this.glossary});
    }

    public closeGlossary(): void {
        if (this.glossaryService.isGlossaryOpened()) {
            this.glossaryService.setGlossaryOpened(false);
            this.eventBus.endActivityEvent(new ActivityEventData({timing: TimerStrategy.glossaryAudio}));
            this.eventBus.endActivityEvent(new ActivityEventData({timing: TimerStrategy.glossaryFact}));
            this.glossaryEnd = new Date();
            if (!this.config.isSuperUserMode) {
                this.log.log("uploading glossary timing");
                this.initiateUploadUserModel();
            }
            this.audioQueueService.reset().subscribe(() => {
                this.reset();
                this.contentCursor.deselectGlossaryPhrase();
            });
        }
    }

    private reset(): void {
        this.resetVideoProcess();
        this.resetHighlight();
        this.resetTab();
        this.glossaryService.setGlossaryOpened(false);
        this.showGlossaryVideo = false;
    }

    public playSpanish(): void {
        let url = this.config.audioUrl + this.spanish.audio;
        this.audioQueueService.reset().subscribe(() => {
            this.resetVideoProcess();
            this.continueReading = false;
            this.resetHighlight();
            this.resetTab();
            this.highlight.spanish = true;
            this.spanishEnabled = true;
            this.eventBus.next(
                new AudioQueueEvent([
                    new AudioQueueItem(url, TranslateAudio.NO, () => this.nextAudioActions("spanish"))
                ], InterruptAudio.NO));
        });
    }

    public showSpanish(): boolean {
        return this.studentConfig.glossaryIsSpanishEnabled;
    }

    public viewSpanish(): boolean {
        return this.spanish && this.spanishEnabled;
    }

    public isVideoSrc(src: string): boolean {
        return src ? src.search(/\.mov$/i) > 0 : false;
    }

    public getVideoSrc(video: string, filetype: string): string {
        // remove the .mov extension from video src
        video = video.toLowerCase();
        return video ? this.config.parseVideoUrl(video.replace(/\.mov$/i, "." + filetype),
                                                 this.glossaryStart.getTime()) : "";
    }

    public getImageSrc(img: string): string {
        return img ? this.config.parseVideoImageUrl(img, this.glossaryStart.getTime()) : "";
    }

    public checkForDidYouKnow(audio): boolean {
        return audio !== "";
    }

    public isImageThere(image): boolean {
        return image !== "";
    }

    public getSyllables(): string[] {
        let sylls = this.glossary.syllables.text.toString();
        let syllbreak = "º·";
        let escapedperiod = "¿";
        let ndash = "º–";
        let spaces = "º ";
        let syllablesArray = sylls.split(' ');
        sylls = syllablesArray.reduce((cur: string, ele: string) => {
            // add '^' at the end to the single syllable like : "run", "top" etc.
            if (ele.match(/^[\w]{1,}$/)) {
                ele += '^';
            }
            return cur += ele + ' ';
        }, "");
        sylls = sylls.trim();

        let sylls1 = sylls.replace("\\\." , escapedperiod ).replace(/\./g , syllbreak );
        let sylls2 = sylls1.replace(/-/g, ndash ).replace(/\s/g, spaces );
        let finalword = sylls2.replace(escapedperiod, ".");
        return finalword.split(/º+/) || [];
    }

    // first expression will match the pattern like : ex^
    // second expression will match the pattern of single word : pet OR animal
    public isSyllableStressed(elem: string): boolean {
        return !!elem.match(/\^$/);
    }

    public getParsedSyllable(elem: string): string {
        return this.isSyllableStressed(elem) ? elem.replace(/\^/g, "") : elem;
    }

    public boldedUsage(): string {
        let usagetext = this.glossary.context.text.toString();
        let searchb = "^#";
        let b1 = "<b>";
        let b2 = "</b>";
        usagetext = usagetext.replace( searchb, b1 );
        usagetext = usagetext.replace( searchb, b2 );
        return usagetext;
    }

    public getGlossaryDefinitionColsBasedOnImage(image): any {
        if (this.isImageThere(image)) {
            return { "col-xs-6": true };
        } else {
            return { "col-xs-12 pl-1": true };
        }
    }

}
